"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PagePartRectIndex = exports.PagePartRectError = void 0;
exports.pagePartEntryPlace = pagePartEntryPlace;
exports.measurePagePartRect = measurePagePartRect;
exports.samePagePartRect = samePagePartRect;
const page_layer_index_1 = require("./page-layer-index");
const page_structure_entry_1 = require("./page-structure-entry");
const page_structure_tree_1 = require("./page-structure-tree");
/** A stored canvas place is no `[x, y, w, h]` rectangle. */
class PagePartRectError extends Error {
    constructor() {
        super(...arguments);
        this.name = 'PagePartRectError';
    }
}
exports.PagePartRectError = PagePartRectError;
function parsedPlace(id, value) {
    if (typeof value !== 'string')
        return undefined;
    try {
        return JSON.parse(value);
    }
    catch (error) {
        if (error instanceof SyntaxError)
            throw new PagePartRectError(`Invalid canvas place of ${id}`);
        throw error;
    }
}
function canvasPlace(id, value) {
    const place = parsedPlace(id, value);
    if (!Array.isArray(place) ||
        place.length !== 4 ||
        !place.every(Number.isFinite) ||
        place[2] < 0 ||
        place[3] < 0)
        throw new PagePartRectError(`Invalid canvas place of ${id}`);
    return place;
}
// A rotated element reaches beyond its stored xywh: the axis-aligned
// rectangle of the place turned by `rotate` degrees about its centre.
function rotatedPlace(id, [x, y, w, h], rotate) {
    if (typeof rotate !== 'number' || !Number.isFinite(rotate))
        throw new PagePartRectError(`Invalid rotation of ${id}`);
    if (rotate % 360 === 0)
        return [x, y, w, h];
    const radians = (rotate * Math.PI) / 180;
    const cos = Math.abs(Math.cos(radians)), sin = Math.abs(Math.sin(radians));
    const width = w * cos + h * sin, height = w * sin + h * cos;
    return [x + (w - width) / 2, y + (h - height) / 2, width, height];
}
// A connector keeps no xywh: its free ends are its places on the canvas;
// an attached end lies where the element it is attached to lies.
function freeEndsPlace(id, fields) {
    const points = ['source', 'target'].flatMap(key => {
        const end = fields.get(key);
        const position = typeof end === 'object' && end !== null
            ? end.position
            : undefined;
        if (position === undefined)
            return [];
        if (!Array.isArray(position) ||
            position.length !== 2 ||
            !position.every(Number.isFinite))
            throw new PagePartRectError(`Invalid ${key} position of ${id}`);
        return [position];
    });
    if (points.length === 0)
        return undefined;
    const xs = points.map(([x]) => x), ys = points.map(([, y]) => y);
    const left = Math.min(...xs), top = Math.min(...ys);
    return [left, top, Math.max(...xs) - left, Math.max(...ys) - top];
}
const rotationOf = (fields, key) => fields.has(key) ? fields.get(key) : 0;
/**
 * The canvas place of entry `id` while it lives in part `partId`: the xywh
 * of a canvas element or the prop:xywh of a block in canvas layer position,
 * turned by its rotation. Undefined for any other entry or without a place.
 */
function pagePartEntryPlace(partId, id, fields, entries) {
    const entry = entries.get(id);
    if (!fields ||
        entry === undefined ||
        entry.s !== partId ||
        !(0, page_structure_tree_1.isPageTreeEntry)(entry) ||
        entry.d !== undefined)
        return undefined;
    if (entry.k === page_structure_entry_1.PAGE_CONNECTOR_KIND)
        return freeEndsPlace(id, fields);
    if ((0, page_structure_entry_1.isPageElementKind)(entry.k)) {
        const value = fields.get('xywh');
        return value === undefined
            ? undefined
            : rotatedPlace(id, canvasPlace(id, value), rotationOf(fields, 'rotate'));
    }
    const parentKind = entry.p === null ? undefined : entries.get(entry.p)?.k;
    const value = fields.get('prop:xywh');
    if (value === undefined || !(0, page_layer_index_1.pageBlockInCanvasLayer)(entry.k, parentKind))
        return undefined;
    // Hydration must expose malformed persisted geometry without inventing a
    // rectangle or aborting measurement of the other blocks in the part.
    let place;
    try {
        place = canvasPlace(id, value);
    }
    catch (error) {
        if (error instanceof PagePartRectError)
            return undefined;
        throw error;
    }
    return rotatedPlace(id, place, rotationOf(fields, 'prop:rotate'));
}
const unite = (a, b) => {
    const left = Math.min(a[0], b[0]), top = Math.min(a[1], b[1]);
    return [
        left,
        top,
        Math.max(a[0] + a[2], b[0] + b[2]) - left,
        Math.max(a[1] + a[3], b[1] + b[3]) - top,
    ];
};
// A place on an edge of the union: when it shrinks or goes, the union may.
const touches = (place, union) => place[0] === union[0] ||
    place[1] === union[1] ||
    place[0] + place[2] === union[0] + union[2] ||
    place[1] + place[3] === union[1] + union[3];
/**
 * The rectangle of one loaded part, kept per changed entry instead of
 * measured from all its fields: a change widens it at once, and only a
 * place that leaves an edge makes it measure its places again.
 */
class PagePartRectIndex {
    constructor() {
        this.places = new Map();
        this.stale = false;
    }
    set(id, place) {
        const previous = this.places.get(id);
        if (previous === place ||
            (previous && place && samePagePartRect(previous, place)))
            return;
        if (place)
            this.places.set(id, place);
        else
            this.places.delete(id);
        if (this.stale)
            return;
        if (previous && this.union && touches(previous, this.union)) {
            this.stale = true;
            return;
        }
        if (place)
            this.union = this.union ? unite(this.union, place) : place;
    }
    rect() {
        if (this.stale) {
            this.union = undefined;
            for (const place of this.places.values())
                this.union = this.union ? unite(this.union, place) : place;
            this.stale = false;
        }
        return this.union;
    }
}
exports.PagePartRectIndex = PagePartRectIndex;
/**
 * The rectangle around the canvas places of the live entries of part
 * `partId`, as pagePartEntryPlace gives them. Undefined for a part without
 * canvas content.
 */
function measurePagePartRect(partId, part, entries) {
    const rects = new PagePartRectIndex();
    for (const [id, fields] of part.elements)
        rects.set(id, pagePartEntryPlace(partId, id, fields, entries));
    for (const [id, fields] of part.blocks)
        rects.set(id, pagePartEntryPlace(partId, id, fields, entries));
    return rects.rect();
}
/** True when two optional rectangles are the same. */
function samePagePartRect(a, b) {
    return a === b || (!!a && !!b && a.every((value, at) => value === b[at]));
}
